"use client";

import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';
import { useRouter, usePathname } from 'next/navigation';
import type { User, LoginCredentials, RegisterData } from '../types';

// Public auth context — covers buyer, vendor, and fpo_connect users only.
// Admin / superadmin auth lives in @/modules/admin/auth/context/admin-auth.context

interface AuthContextType {
  user: User | null;
  loading: boolean;
  activeRole: 'buyer' | 'vendor' | 'fpo_connect' | null;
  login: (credentials: LoginCredentials, redirectTo?: string) => Promise<void>;
  register: (userData: RegisterData) => Promise<{ message: string; email: string }>;
  verifyOtp: (email: string, otp: string) => Promise<void>;
  updateProfile: (profileData: Partial<User>) => Promise<void>;
  logout: () => Promise<void>;
  switchRole: (role: 'buyer' | 'vendor' | 'fpo_connect') => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeRole, setActiveRole] = useState<'buyer' | 'vendor' | 'fpo_connect' | null>(null);
  const router = useRouter();
  const pathname = usePathname();

  // Synchronize activeRole with current path or user database role
  useEffect(() => {
    if (user) {
      if (user.role === 'fpo_connect') {
        if (pathname.startsWith('/Vendor')) {
          setActiveRole('vendor');
        } else if (pathname.startsWith('/FPO')) {
          setActiveRole('fpo_connect');
        } else {
          // If on public pages, check localStorage preference or default to 'buyer'
          const stored = typeof window !== 'undefined' ? localStorage.getItem('kab_active_role') : null;
          if (stored === 'vendor' || stored === 'buyer' || stored === 'fpo_connect') {
            setActiveRole(stored as any);
          } else {
            setActiveRole('fpo_connect'); // Default mode for FPO Connect user
          }
        }
      } else {
        setActiveRole(user.role || 'buyer');
      }
    } else {
      setActiveRole(null);
    }
  }, [user, pathname]);

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Clean up stale localStorage entries if present
        if (typeof window !== 'undefined') {
          localStorage.removeItem('auth_user');
          localStorage.removeItem('auth_token');
        }

        // On page load, the in-memory access_token is empty.
        // Attempt a silent refresh using the HttpOnly refresh_token cookie.
        const refreshed = await authService.refreshAccessToken();
        if (!refreshed) {
          // No valid refresh_token → user is not logged in
          setUser(null);
          setLoading(false);
          return;
        }

        // Now we have a fresh access_token in memory — fetch the profile
        const data = await authService.getProfile();
        if (data && data.user) {
          setUser(data.user);
        } else {
          setUser(null);
          authService.removeToken();
        }
      } catch (err) {
        console.error("❌ [AuthContext] Error during initAuth:", err);
        setUser(null);
        authService.removeToken();
      } finally {
        setLoading(false);
      }
    };
    initAuth();
  }, []);

 const login = async (credentials: LoginCredentials, redirectTo?: string) => {
    try {
      const data = await authService.login(credentials);
      setUser(data.user);

      if (redirectTo) {
        router.push(redirectTo);
        return;
      }

      const role = data.user.role || 'buyer';
      if (role === 'vendor') {
        router.push('/Vendor');
      } else if (role === 'fpo_connect') {
        router.push('/FPO');
      } else {
        router.push('/');
      }
    } catch (error) {
      throw error;
    }
  };

  const register = async (userData: RegisterData) => {
    try {
      const data = await authService.register(userData);
      return data; // Returns { message: '...', email: '...' }
    } catch (error) {
      throw error;
    }
  };

  const verifyOtp = async (email: string, otp: string) => {
    try {
      const data = await authService.verifyOtp(email, otp);
      setUser(data.user);

      const role = data.user.role || 'buyer';
      if (role === 'vendor') {
        router.push('/Vendor');
      } else if (role === 'fpo_connect') {
        router.push('/FPO');
      } else {
        router.push('/');
      }
    } catch (error) {
      throw error;
    }
  };

  const updateProfile = async (profileData: Partial<User>) => {
    try {
      const data = await authService.updateProfile(profileData);
      if (data && data.user) {
        setUser(data.user);
      }
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
    if (typeof window !== 'undefined') {
      localStorage.removeItem('kab_active_role');
    }
    router.push('/auth/login');
  };

  const switchRole = (role: 'buyer' | 'vendor' | 'fpo_connect') => {
    if (!user || user.role !== 'fpo_connect') return;

    setActiveRole(role);
    if (typeof window !== 'undefined') {
      localStorage.setItem('kab_active_role', role);
    }

    if (role === 'fpo_connect') {
      router.push('/FPO');
    } else {
      router.push('/');
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, activeRole, login, register, verifyOtp, updateProfile, logout, switchRole }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
